import {
  BLOOM_MS,
  CROSSFADE_MS,
  LIFT_MS,
  useSplashSequence,
} from '@/hooks/use-splash-sequence';
import { getSplashAsset } from '@/utils/splash-asset';
import { Image } from 'expo-image';
import React, { useEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Defs, RadialGradient, Rect, Stop } from 'react-native-svg';

/** Must match `expo-splash-screen`'s `imageWidth` in app.config.ts, or the
 *  hand-off from the native splash visibly jumps on iOS. */
const LOGO_SIZE = 200;

/** The app background. The native splash resolves to this same field. */
const BACKGROUND = '#080706';
/** The one brand green — a candle catching behind the sprig mark. */
const GLOW = '#9ACD32';

const GLOW_SIZE = 340;
/**
 * Lifts the glow off centre to sit behind the sprig, not the wordmark.
 *
 * Measured from the asset's alpha channel, not eyeballed: in the 1024px canvas
 * the sprig's leaves span rows 331-425 (mass centred on y≈378), its stem
 * descends thinly to ~475, and the wordmark's x-height runs 530-645. The image
 * is square and `contentFit="contain"` into a LOGO_SIZE box, so the canvas maps
 * at LOGO_SIZE / 1024, and the sprig sits (378 - 512) * scale above centre.
 */
const GLOW_OFFSET_Y = Math.round((378 - 512) * (LOGO_SIZE / 1024));

const GLOW_PEAK = 0.28;
const GLOW_TROUGH = 0.16;
const PULSE_MS = 2000;
const BREATH_SCALE = 1.03;
const LIFT_DISTANCE = -12;

/**
 * The splash must out-stack everything mountable during boot. `elevation` is
 * not redundant with `zIndex`: on Android an elevated sibling draws above a
 * non-elevated one whatever its zIndex, and the Discover screen mounts at
 * elevation 1001 — which can happen while the splash is still inside its
 * minimum-display window on a fast auth resolve.
 *
 * NOTE: this repo has no z-index scale; it has competing 99999 / 9999 / 9998 /
 * 1001 magic numbers. This value only has to top the *boot-time* stack (the
 * 99999 crop modals are unreachable until authenticated), so it is deliberately
 * not part of that arms race.
 */
const SPLASH_STACK_LEVEL = 10000;

const EASE_OUT_QUART = Easing.out(Easing.poly(4));

interface AnimatedSplashProps {
  /** True while the caller still needs the splash held. */
  visible: boolean;
  /** Fires once the lift completes, so the caller can unmount the overlay. */
  onFinish?: () => void;
}

/**
 * The bridge between the native splash screen and the app.
 *
 * Mounts as a pixel-match of the native splash, blooms a soft green glow while
 * auth resolves (this replaces a spinner), then lifts away. Under reduced
 * motion it degrades to a plain crossfade but still covers the auth wait.
 */
function AnimatedSplash({ visible, onFinish }: AnimatedSplashProps) {
  const reducedMotion = useReducedMotion();
  const { phase } = useSplashSequence({ visible, reducedMotion, onFinish });

  const overlayOpacity = useSharedValue(1);
  const lockupOpacity = useSharedValue(1);
  const lockupScale = useSharedValue(1);
  const lockupTranslateY = useSharedValue(0);
  const glowOpacity = useSharedValue(0);

  useEffect(() => {
    if (phase === 'bloom') {
      // The candle catches: glow settles in, lockup breathes once. No spin.
      // Durations derive from BLOOM_MS — the hook advances to `hold` on that
      // clock, so a hardcoded duration here would silently drift out of step.
      glowOpacity.set(withTiming(GLOW_PEAK, {
        duration: BLOOM_MS,
        easing: EASE_OUT_QUART,
      }));
      lockupScale.set(withSequence(
        withTiming(BREATH_SCALE, { duration: BLOOM_MS / 2, easing: EASE_OUT_QUART }),
        withTiming(1, { duration: BLOOM_MS / 2, easing: EASE_OUT_QUART }),
      ));
      return;
    }

    if (phase === 'hold') {
      if (reducedMotion) return;
      // Slow pulse stands in for a spinner while auth resolves.
      glowOpacity.set(withRepeat(
        withTiming(GLOW_TROUGH, {
          duration: PULSE_MS,
          easing: Easing.inOut(Easing.quad),
        }),
        -1,
        true,
      ));
      return;
    }

    if (phase === 'lift') {
      if (reducedMotion) {
        overlayOpacity.set(withTiming(0, { duration: CROSSFADE_MS }));
        return;
      }
      const config = { duration: LIFT_MS, easing: EASE_OUT_QUART };
      lockupTranslateY.set(withTiming(LIFT_DISTANCE, config));
      lockupOpacity.set(withTiming(0, config));
      glowOpacity.set(withTiming(0, config));
      overlayOpacity.set(withTiming(0, config));
    }
  }, [
    phase,
    reducedMotion,
    glowOpacity,
    lockupScale,
    lockupOpacity,
    lockupTranslateY,
    overlayOpacity,
  ]);

  const overlayAnimatedStyle = useAnimatedStyle(() => ({
    opacity: overlayOpacity.get(),
  }));

  const lockupAnimatedStyle = useAnimatedStyle(() => {
    // Reduced motion: opacity only — never a transform.
    if (reducedMotion) {
      return { opacity: lockupOpacity.get() };
    }
    return {
      opacity: lockupOpacity.get(),
      transform: [
        { translateY: lockupTranslateY.get() },
        { scale: lockupScale.get() },
      ],
    };
  });

  const glowAnimatedStyle = useAnimatedStyle(() => ({
    opacity: glowOpacity.get(),
  }));

  return (
    <Animated.View
      testID="animated-splash"
      pointerEvents="none"
      style={[styles.overlay, overlayAnimatedStyle]}
    >
      {!reducedMotion && (
        <Animated.View
          testID="animated-splash-glow"
          pointerEvents="none"
          style={[styles.glow, glowAnimatedStyle]}
        >
          <Svg width={GLOW_SIZE} height={GLOW_SIZE}>
            <Defs>
              <RadialGradient id="splash-glow" cx="50%" cy="50%" r="50%">
                <Stop offset="0%" stopColor={GLOW} stopOpacity={1} />
                <Stop offset="55%" stopColor={GLOW} stopOpacity={0.35} />
                <Stop offset="100%" stopColor={GLOW} stopOpacity={0} />
              </RadialGradient>
            </Defs>
            <Rect
              x="0"
              y="0"
              width={GLOW_SIZE}
              height={GLOW_SIZE}
              fill="url(#splash-glow)"
            />
          </Svg>
        </Animated.View>
      )}

      <Animated.View testID="animated-splash-lockup" style={lockupAnimatedStyle}>
        <Image
          testID="animated-splash-logo"
          source={getSplashAsset()}
          style={styles.logo}
          contentFit="contain"
          // The native splash is already on screen; fading in would double up.
          transition={0}
        />
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    // Written out rather than spread from `StyleSheet.absoluteFillObject`,
    // which is undefined under the jest-expo RN mock and would silently
    // spread to nothing.
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: BACKGROUND,
    zIndex: SPLASH_STACK_LEVEL,
    elevation: SPLASH_STACK_LEVEL,
  },
  glow: {
    position: 'absolute',
    width: GLOW_SIZE,
    height: GLOW_SIZE,
    transform: [{ translateY: GLOW_OFFSET_Y }],
  },
  logo: {
    width: LOGO_SIZE,
    height: LOGO_SIZE,
  },
});

export { AnimatedSplash };
export type { AnimatedSplashProps };
